<template>
  <g
    v-if="width > 0"
    class="z-10 origin-center"
    :style="{
      transform: 'rotate(' + skillStartAngle + ') ',
    }"
  >
    <path
      :d="dBackground"
      class="stroke-surface-container-low"
      :class="[!hovered && 'fill-' + color + '-95', hovered && 'fill-' + color + '-90']"
    />
    <path
      :d="dValue(value, maxValue)"
      class="ease-spatial origin-center transition-all duration-400"
      :class="[colorClasses, value == null && 'opacity-35']"
      :style="{
        transitionDelay: (index / totalNumberOfSegments) * 200 + 'ms',
      }"
    />
  </g>
</template>
<script lang="ts" setup>
  import type { Perspective } from '@/core/models/perspective'
  import { computed } from 'vue'

  interface Props {
    color: string
    index: number
    width: number
    hovered: boolean
    maxValue: number
    totalNumberOfSegments: number
    value: number | null
    /** Menon color shade for the value wedge. Defaults to 60. */
    valueShade?: string
    allPerspectives?: ReadonlyArray<Perspective>
    perspective?: Perspective | string | null
  }

  const props = defineProps<Props>()

  const STROKE_WIDTH_GRID = 2

  const MIN_SCALE_FOR_NULL_VALUE = 0.04

  const skillStartAngle = computed(() => {
    return props.index * skillAngle.value + 'rad'
  })

  const radius = computed(() => {
    return props.width / 2
  })

  const skillAngle = computed(() => {
    return (2 * Math.PI) / props.totalNumberOfSegments
  })

  const valueAngle = computed(() => {
    return skillAngle.value - 2 * (STROKE_WIDTH_GRID / radius.value)
  })

  const dBackground = computed(() => {
    const angle = skillAngle.value / 2
    const x = Math.sin(angle) * radius.value
    const y = Math.cos(angle) * radius.value

    return `M ${radius.value} ${radius.value} l -${x} -${y} a ${radius.value} ${radius.value} 0 0 1 ${2 * x} 0 L ${radius.value} ${radius.value}`
  })

  const calculateScale = (value: number | null, maxValue: number) => {
    if (value == null) {
      return 1 / (maxValue + 1) + MIN_SCALE_FOR_NULL_VALUE
    }
    return (value * (maxValue - 1)) / (maxValue + 1) + 2 * (1 / (maxValue + 1))
  }

  const borderRadius = (scale: number) => {
    const circumference = 2 * Math.PI * radius.value * scale
    return circumference / props.totalNumberOfSegments / 7
  }

  const colorClasses = computed(() => {
    if (props.perspective && props.allPerspectives && props.allPerspectives.length > 0) {
      const index = props.allPerspectives.findIndex((p) => p.key === props.perspective)
      if (index === 0) {
        return `fill-${props.color}-40`
      }
      if (index === 1) {
        return `fill-${props.color}-60`
      }
      return `fill-${props.color}-80`
    }
    return `fill-${props.color}-${props.valueShade ?? '60'}`
  })

  const dValue = (value: number | null, maxValue: number) => {
    const scale = calculateScale(value, maxValue)
    const ARC_RADIUS = borderRadius(scale)
    const angle = valueAngle.value / 2
    const valueRadius = radius.value * scale

    const a = {
      x: Math.sin(angle) * (valueRadius - ARC_RADIUS),
      y: Math.cos(angle) * (valueRadius - ARC_RADIUS),
    }
    const b = {
      x: a.x - Math.sin(angle) * valueRadius,
      y: a.y - Math.cos(angle) * valueRadius,
    }
    const c = {
      x: a.x - Math.sin(angle - ARC_RADIUS / valueRadius) * valueRadius,
      y: a.y - Math.cos(angle - ARC_RADIUS / valueRadius) * valueRadius,
    }
    const d = {
      x: Math.sin(angle - ARC_RADIUS / valueRadius) * valueRadius * 2,
      y: a.x - c.y - Math.cos(ARC_RADIUS / valueRadius) * valueRadius,
    }

    return `M ${radius.value} ${radius.value} l ${-a.x} ${-a.y} q ${b.x} ${b.y} ${c.x} ${c.y} a ${valueRadius} ${valueRadius} 0 0 1 ${d.x} 0 q ${-(b.x - c.x)} ${b.y - c.y} ${c.x} ${-c.y} L ${radius.value} ${radius.value}`
  }
</script>
